import { useEffect, useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';

import apiClient from '../../../apiClient';
import { ITaskItem } from '../../../globals/interfaces';
import TaskModal from './TaskModal'; 
import Layout from '../../../components/custom/Layout';
import _ from 'lodash';

const fetcher = async (endpoint: string) => {
    try {
        const { statusText, data } = await apiClient.get(endpoint);
        if (statusText === 'OK') {
            return data;
        }
        return null;
    } catch (err) {
        throw err;
    }
}

interface Props {
    displayMode: boolean;
}

export default function TasksListContent ({
    displayMode
}: Props) {

    const { data: workItems } = useSWR(
        `/work-items`,
        fetcher,
        {
            refreshInterval: 600000
        }
    );

    const [modal, setModal] = useState(false);
    const [workItemsMap, setWorkItemsMap] = useState<
        Record<string, Array<ITaskItem>>
    >({});
    const [selectedItem, setSelectedItem] = useState<ITaskItem>();
    const boardColumns = ['To Do', 'Develop', 'Test', 'Confirm'];

    const resolveWorkItemsMap = (arr: Array<ITaskItem>) => {
        const map: Record<string, Array<ITaskItem>> = {};
        for (const item of arr) {
            const { boardColumn } = item;
            if (!map[boardColumn]) {
                map[boardColumn] = [];
            }
            map[boardColumn].push(item);
        }
        return map;
    };

    const chunkArray = (arr: Array<ITaskItem>, chunkSize: number) => {
        let res = [];
        for(let i = 0; i < arr.length; i += chunkSize)
          res.push(arr.slice(i, i + chunkSize));
        return res;
    }
      
    const doneItems = _.get(workItemsMap, ['Done'], []);
    const chunkedItems = chunkArray(doneItems, 15);

    useEffect(() => {
        if (workItems) {
            setWorkItemsMap(resolveWorkItemsMap(workItems));
        }
    }, [workItems]);

    const handleViewItem = (item: ITaskItem) => {
        setModal(true);
        setSelectedItem(item);
    };

    return (
        <OuterContainer displayMode={displayMode}>
            <LanesContainer>
                {boardColumns.map((cat, i) => (
                    <Lane key={i} color={colorsMap[cat]} displayMode={displayMode}>
                        <BoardColumnName>{cat}: {_.get(workItemsMap, [cat], []).length}</BoardColumnName>
                        {_.get(workItemsMap, [cat], []).map((item, i) => (
                                <WorkItem
                                    item={item}
                                    key={i}
                                    handleViewItem={handleViewItem}
                                />
                            ))}
                    </Lane>
                ))}
            </LanesContainer>
            <DoneContainer color={colorsMap['Done']} displayMode={displayMode}>
                <BoardColumnName>Done: {_.get(workItemsMap, ['Done'], []).length}</BoardColumnName>
                <DoneContainerFlex>
                    {
                        chunkedItems.map((array, i) => (
                            <div key={i}>
                                {
                                    array.map((item, j) => (
                                        <WorkItem
                                            item={item}
                                            key={j}
                                            handleViewItem={handleViewItem}
                                        />
                                    ))
                                }
                            </div>
                        ))
                    }
                </DoneContainerFlex>
            </DoneContainer>
            <TaskModal 
                modal={modal}
                setModal={setModal}
                workItem={selectedItem}
            />
        </OuterContainer>
    );
}

interface WorkItemProps {
    item: ITaskItem;
    handleViewItem: (item: ITaskItem) => void;
}

const WorkItem = ({ item, handleViewItem }: WorkItemProps) => {

    const assignedTo = _.get(item, ['assignedTo'], '').substring(0, 1);

    return (
        <div>
            <h6 className={'hover'} onClick={() => handleViewItem(item)}>
                <Icon
                    className={
                        item.workItemType === 'Bug'
                            ? 'fa-duotone fa-bug'
                            : 'fa-duotone fa-star'
                    }
                    bug={item.workItemType === 'Bug'}
                />{' '}
                <b>{item.id}</b> {assignedTo && `(${assignedTo})`} <i>[{item.project}]</i> {item.title}
            </h6>
        </div>
    );
};

const colorsMap: Record<string, string> = {
    'To Do': 'B7E4C7',
    'Develop': '74C69D',
    'Test': '52B788',
    'Confirm': '40916C',
    'Done': 'ADB5BD'
}

const LanesContainer = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
    flex: 2;
`;

const Lane = styled.div<{color: string, displayMode: boolean}>`
    background-color: ${p => p.displayMode ? '#000' : '#' + p.color};
    border: 3px solid ${p => p.displayMode ? '#7d7b7b' : '#000'};
    border: 3px solid black;
    padding: 10px;
    overflow-y: scroll;
    flex: 1;
`;

const BoardColumnName = styled.h2`
    font-weight: bold;
`;

const Icon = styled.i<{bug: Boolean}>`
    color: ${p => p.bug ? 'red' : '#e85d04'};
`;

const DoneContainer = styled.div<{color: string, displayMode: boolean}>`
    background-color: ${p => p.displayMode ? '#000' : '#' + p.color};
    color: ${p => p.displayMode ? '#fff' : '#000'};
    border: 3px solid ${p => p.displayMode ? '#7d7b7b' : '#000'};
    padding: 10px;
    overflow-y: scroll;
    flex: 1;
`;

const DoneContainerFlex = styled.div`
    display: flex;
    gap: 25px;
`;

const OuterContainer = styled.div<{displayMode: boolean}>`
    display: flex;
    flex-direction: column;
    height: 100vh;
    background-color: ${p => p.displayMode ? '#000' : '#fff'};
    color: ${p => p.displayMode ? '#fff' : '#000'};
`;